import { Plan, Service } from "@/generated/graphql_billing";
import { createContext, ReactNode, useMemo } from "react";

interface Props {
  children: ReactNode;
}

interface SelectPlanContextObj {
  showFeaturesFor: {
    [serviceCode: string]: { [featureCode: string]: boolean };
  };
}

export const SelectPlanContext = createContext<SelectPlanContextObj>({
  showFeaturesFor: {},
});

// DEPRECATED: use calcShowFeaturesFor instead
export const calcHideFeaturesFor = (services: Service[], plans: Plan[]) => {
  if (!services || !plans) return {};

  const obj = {};

  for (const service of services) {
    obj[service.code] =
      plans.flatMap((plan) =>
        plan.services
          .filter((s) => s.code === service.code)
          .flatMap((s) => s.planFeatures)
      ).length === 0;
  }

  return obj;
};

// calcShowFeaturesFor calculates which features should be shown for all services.
// The key idea is that, if a feature is not available in ANY plans, it should not
// be shown at all - even in a disabled/not available for plan state.
//
// This facilitates the addition of new features as new feature codes can be safely
// added without affecting the FE UI until the new feature is enabled for at least
// one plan.
//
// calcShowFeaturesFor takes an array of all services available for a bank and an
// array of all active plans for the bank.
//
// It is used by pages that need to display all available plans for a financial
// institution and their corresponding features.
//
// Currently, this includes:
// - ChangePlanPage
// - SelectPlanPage
export const calcShowFeaturesFor = (services: Service[], plans: Plan[]) => {
  if (!services || !plans) return {};

  const obj = {};

  for (const service of services) {
    obj[service.code] = {};
    for (const plan of plans) {
      const planService = plan.services.find((s) => s.code === service.code);
      if (planService) {
        for (const feature of planService.planFeatures) {
          obj[service.code][feature.code] = true;
        }
      }
    }
  }

  return obj;
};

export const useSelectPlan = (services: Service[], plans: Plan[]) => {
  const showFeaturesFor = calcShowFeaturesFor(services, plans);

  const SelectPlan = ({ children }: Props) => {
    const ctx = {
      showFeaturesFor,
    };
    return (
      <SelectPlanContext.Provider value={ctx}>
        {children}
      </SelectPlanContext.Provider>
    );
  };

  return SelectPlan;
};
